/**
 * Global friendly date & time pickers.
 *
 *   - <input type="date">  ->  flatpickr branded calendar
 *   - <input type="time">  ->  timepicker-ui (Android / Material-style clock dial)
 *
 * Both are auto-applied to every matching input on the page, including inputs added
 * dynamically after load (quote spec fields, sub-contractor onboarding, etc.) via a
 * MutationObserver.
 *
 * Submitted values stay byte-for-byte compatible with the native inputs so the Django
 * DateField / TimeField keep parsing them:
 *   - date  ->  "Y-m-d"  (e.g. 2026-06-28)
 *   - time  ->  "H:i"    (24h, e.g. 14:30)
 *
 * For time, the original input is kept as a hidden field holding the 24h value while a
 * visible Material clock input shows the friendly 12h value (e.g. "2:30 PM").
 *
 * Opt out of a single field with `data-no-flatpickr`.
 * Force "today" as the earliest selectable date with `data-fp-min-today` (booking forms).
 */
(function () {
    'use strict';

    var DATE_ALT_FORMAT = 'D, j M Y';

    function pad2(n) {
        return String(n).padStart(2, '0');
    }

    function isReady(el) {
        return el.dataset.fpReady === '1';
    }

    function markReady(el) {
        el.dataset.fpReady = '1';
    }

    /* ----------------------------- date (flatpickr) ----------------------------- */

    function resolveMinDate(el) {
        if (el.dataset.fpMinToday !== undefined) return 'today';
        return el.getAttribute('min') || undefined;
    }

    function resolveMaxDate(el) {
        // `data-fp-max-days="N"` caps selection at today + N days (booking forms);
        // otherwise fall back to a plain `max` attribute.
        var days = parseInt(el.dataset.fpMaxDays, 10);
        if (!isNaN(days)) {
            var d = new Date();
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + days);
            return d;
        }
        return el.getAttribute('max') || undefined;
    }

    function showBackdrop(instance) {
        document.body.classList.add('fp-scroll-lock');
        document.documentElement.style.overflow = 'hidden';
        var backdrop = document.createElement('div');
        backdrop.className = 'fp-backdrop';
        backdrop.addEventListener('click', function () {
            instance.close();
        });
        document.body.appendChild(backdrop);
        // Store on the instance so concurrent pickers each manage their own backdrop.
        instance._fpBackdrop = backdrop;
        // next frame -> fade in
        window.requestAnimationFrame(function () {
            if (backdrop) backdrop.classList.add('is-visible');
        });
    }

    function hideBackdrop(instance) {
        var backdrop = instance && instance._fpBackdrop;
        if (backdrop && backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
        if (instance) instance._fpBackdrop = null;
        // Only release the body scroll-lock once no picker backdrops remain in the DOM.
        if (!document.querySelector('.fp-backdrop')) {
            document.documentElement.style.overflow = '';
            document.body.classList.remove('fp-scroll-lock');
        }
    }

    function syncYearDropdown(instance) {
        var sel = instance.calendarContainer.querySelector('.fp-year-dd');
        if (sel) sel.value = instance.currentYear;
    }

    function buildYearDropdown(instance) {
        var current = instance.calendarContainer.querySelector('.flatpickr-current-month');
        if (!current || current.querySelector('.fp-year-dd')) return;

        var nowYear = instance.currentYear;
        var minYear = instance.config.minDate ? instance.config.minDate.getFullYear() : nowYear - 100;
        var maxYear = instance.config.maxDate ? instance.config.maxDate.getFullYear() : nowYear + 10;
        if (maxYear < minYear) maxYear = minYear;

        var select = document.createElement('select');
        select.className = 'fp-year-dd';
        select.setAttribute('aria-label', 'Year');
        for (var y = minYear; y <= maxYear; y++) {
            var opt = document.createElement('option');
            opt.value = String(y);
            opt.textContent = String(y);
            select.appendChild(opt);
        }
        select.value = String(instance.currentYear);
        select.addEventListener('change', function () {
            var y = parseInt(select.value, 10);
            instance.jumpToDate(new Date(y, instance.currentMonth, 1), false);
            syncYearDropdown(instance);
        });
        current.appendChild(select);
    }

    function presentAsModal(instance) {
        var cal = instance.calendarContainer;
        cal.classList.add('fp-modal');
        // Add a Material-style "Select a date" header once, mirroring the time dialog.
        if (!cal.querySelector('.fp-modal-title')) {
            var title = document.createElement('div');
            title.className = 'fp-modal-title';
            title.textContent = 'Select a date';
            cal.insertBefore(title, cal.firstChild);
        }
        showBackdrop(instance);
    }

    function initDate(el) {
        var cfg = {
            disableMobile: true, // branded flatpickr UI everywhere, including mobile
            altInput: true,
            altInputClass: el.className, // mirror styling onto the visible altInput
            dateFormat: 'Y-m-d',
            altFormat: DATE_ALT_FORMAT,
            appendTo: document.body, // detach so fixed-position centering is reliable
            monthSelectorType: 'dropdown', // month is a <select> (year dropdown added below)
            onReady: [function (selectedDates, dateStr, instance) {
                buildYearDropdown(instance);
            }],
            onMonthChange: [function (selectedDates, dateStr, instance) {
                syncYearDropdown(instance);
            }],
            onYearChange: [function (selectedDates, dateStr, instance) {
                syncYearDropdown(instance);
            }],
            onOpen: [function (selectedDates, dateStr, instance) {
                presentAsModal(instance);
            }],
            onClose: [function (selectedDates, dateStr, instance) {
                hideBackdrop(instance);
            }],
        };
        var minDate = resolveMinDate(el);
        if (minDate) cfg.minDate = minDate;
        var maxDate = resolveMaxDate(el);
        if (maxDate) cfg.maxDate = maxDate;
        var instance = window.flatpickr(el, cfg);
        // flatpickr's altInput drops the placeholder — restore a friendly hint.
        if (instance && instance.altInput) {
            instance.altInput.placeholder = el.getAttribute('placeholder') || 'Select a date';
        }
    }

    /* ------------------------- time (Material clock dial) ------------------------ */

    function to24h(hour, minutes, type) {
        var h = parseInt(hour, 10) || 0;
        if (type === 'PM' && h !== 12) h += 12;
        if (type === 'AM' && h === 12) h = 0;
        return pad2(h) + ':' + pad2(minutes);
    }

    function to12h(value) {
        var parts = (value || '').split(':');
        if (parts.length < 2) return '';
        var h = parseInt(parts[0], 10);
        if (isNaN(h)) return '';
        var minutes = pad2(parseInt(parts[1], 10) || 0);
        var type = h >= 12 ? 'PM' : 'AM';
        var h12 = h % 12;
        if (h12 === 0) h12 = 12;
        return pad2(h12) + ':' + minutes + ' ' + type;
    }

    function initTime(el) {
        // Build a visible Material clock input; keep the original as a hidden 24h field.
        var display = document.createElement('input');
        display.type = 'text';
        display.readOnly = true;
        display.className = el.className;
        display.placeholder = el.getAttribute('placeholder') || 'Select a time';
        display.setAttribute('aria-label', 'Select a time');
        display.setAttribute('autocomplete', 'off');
        if (el.value) display.value = to12h(el.value);

        el.type = 'hidden';
        el.parentNode.insertBefore(display, el.nextSibling);

        var picker = new window.TimepickerUI(display, {
            clock: { type: '12h' },
            ui: { theme: 'm3-green' },
        });
        picker.create();

        picker.on('confirm', function (data) {
            el.value = to24h(data.hour, data.minutes, data.type);
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Expose for programmatic use / tests.
        el._timepicker = picker;
        display._timepicker = picker;
        // Reach the visible element from the hidden original (validation/focus targets).
        el._timepickerDisplay = display;
        markReady(display);
    }

    /**
     * Resolve the visible element for a date/time field that may have been upgraded.
     * Upgraded time inputs become hidden (visible element on `_timepickerDisplay`);
     * upgraded date inputs hide and expose flatpickr's `altInput`. Falls back to the
     * element itself when no upgrade happened.
     */
    window.visibleDateTimeInput = function (el) {
        return (el && (el._timepickerDisplay || (el._flatpickr && el._flatpickr.altInput))) || el;
    };

    /* --------------------------------- wiring ---------------------------------- */

    function initOne(el) {
        if (!el || el.dataset.noFlatpickr !== undefined || isReady(el) || el._flatpickr) return;
        var type = (el.getAttribute('type') || '').toLowerCase();
        try {
            if (type === 'date') {
                if (typeof window.flatpickr !== 'function') return;
                initDate(el);
            } else if (type === 'time') {
                if (typeof window.TimepickerUI !== 'function') return;
                initTime(el);
            } else {
                return;
            }
            markReady(el);
        } catch (e) {
            if (window.console) console.warn('[datetime-pickers] init failed', el, e);
        }
    }

    function initAll(root) {
        var scope = root && root.querySelectorAll ? root : document;
        scope.querySelectorAll('input[type="date"], input[type="time"]').forEach(initOne);
    }

    function start() {
        // Nothing to do if neither library loaded — native inputs remain as a fallback.
        if (typeof window.flatpickr !== 'function' && typeof window.TimepickerUI !== 'function') return;
        initAll(document);

        if (typeof MutationObserver === 'function') {
            // Coalesce a burst of DOM mutations into a single rAF-debounced sweep instead
            // of running querySelectorAll per added subtree. The fpReady guard keeps
            // initOne idempotent, so re-scanning the whole document stays cheap & correct.
            var scheduled = false;
            var schedule =
                typeof window.requestAnimationFrame === 'function'
                    ? function (fn) { window.requestAnimationFrame(fn); }
                    : function (fn) { window.setTimeout(fn, 0); };

            var observer = new MutationObserver(function (mutations) {
                if (scheduled) return;
                // Only schedule when an element node was actually added.
                var relevant = false;
                for (var i = 0; i < mutations.length && !relevant; i++) {
                    var nodes = mutations[i].addedNodes;
                    for (var j = 0; j < nodes.length; j++) {
                        if (nodes[j].nodeType === 1) { relevant = true; break; }
                    }
                }
                if (!relevant) return;
                scheduled = true;
                schedule(function () {
                    scheduled = false;
                    initAll(document);
                });
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
